import React, { useState, useRef, useEffect } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  where,
  orderBy,
  query,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signOut,
} from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Nav from "../components/Nav";
import SEO from "../components/SEO";
import ArticleImage from "../components/ArticleImage";
import { auth, db, storage } from "../firebase";
import {
  getPublicationDate,
  isArticlePublished,
} from "../utils/articlePublishing";

const ADMIN_EMAIL = "recoverycompassnh@gmail.com";
const ADMIN_SIGN_IN_URL = "https://www.recoverycompassnh.com/admin";
const REPORT_REASON_LABELS = {
  website: "Website",
  phone: "Phone number",
  address: "Address",
  closed: "May be closed",
  details: "Services/details",
  other: "Other",
};

const AdminDashboard = () => {
  // ALL useState and useRef hooks MUST be at the top - no exceptions!
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [loginMessage, setLoginMessage] = useState("");
  const [loginError, setLoginError] = useState("");
  const [articles, setArticles] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportFilter, setReportFilter] = useState("open");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentArticle, setCurrentArticle] = useState({
    title: "",
    slug: "",
    category: "blog-posts",
    author: "",
    excerpt: "",
    content: "",
    image: null,
    imageUrl: "",
    socialImageUrl: "",
    featured: false,
    opinionArticle: false,
    date: getPublicationDate(),
  });

  const [editingId, setEditingId] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState("#ffffff");
  const [contentImageLayout, setContentImageLayout] = useState("center");
  const [contentImageSize, setContentImageSize] = useState("medium");
  const [contentImageWidth, setContentImageWidth] = useState(65);
  const [contentImageHeight, setContentImageHeight] = useState(null);
  const [contentImageAlt, setContentImageAlt] = useState("");
  const [hasSelectedContentImage, setHasSelectedContentImage] = useState(false);
  const contentRef = useRef(null);
  const fileInputRef = useRef(null);
  const contentImageInputRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const selectedContentImageRef = useRef(null);

  const categories = [
    { id: "unsung-heroes", label: "Unsung Heroes" },
    { id: "stories-of-recovery", label: "Stories of Recovery" },
    { id: "blog-posts", label: "Blog Posts" },
  ];

  useEffect(() => {
    let active = true;
    let unsubscribe = () => {};

    const initializeAdminSession = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        try {
          await signInWithEmailLink(auth, ADMIN_EMAIL, window.location.href);
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error("Unable to complete admin sign-in:", error);
          if (active) {
            setLoginError("That sign-in link is invalid or has expired. Request a new link below.");
          }
        }
      }

      if (!active) return;
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        const isAuthorizedAdmin =
          user?.email === ADMIN_EMAIL && user.emailVerified === true;

        if (user && !isAuthorizedAdmin) {
          await signOut(auth);
        }

        if (active) {
          setIsAuthenticated(isAuthorizedAdmin);
          setAuthChecking(false);
        }
      });
    };

    initializeAdminSession();
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "articles"), orderBy("date", "desc"));
      const querySnapshot = await getDocs(q);
      const articlesData = [];
      querySnapshot.forEach((doc) => {
        articlesData.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      setArticles(articlesData);
    } catch (error) {
      console.error("Error loading articles:", error);
      alert("Error loading articles. Check console for details.");
    }
    setLoading(false);
  };

  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const reportQuery = query(
        collection(db, "resourceReports"),
        orderBy("createdAt", "desc"),
        limit(200)
      );
      const reportSnapshot = await getDocs(reportQuery);
      setReports(
        reportSnapshot.docs.map((reportDocument) => ({
          id: reportDocument.id,
          ...reportDocument.data(),
        }))
      );
    } catch (error) {
      console.error("Error loading resource reports:", error);
      alert("Error loading resource reports. Check console for details.");
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    loadArticles();
    loadReports();
  }, [isAuthenticated]);

  const handleSendLoginLink = async () => {
    setLoginMessage("");
    setLoginError("");

    try {
      await sendSignInLinkToEmail(auth, ADMIN_EMAIL, {
        url: ADMIN_SIGN_IN_URL,
        handleCodeInApp: true,
      });
      setLoginMessage(`A secure sign-in link was sent to ${ADMIN_EMAIL}.`);
    } catch (error) {
      console.error("Unable to send admin sign-in link:", error);
      setLoginError("The sign-in link could not be sent. Please wait a moment and try again.");
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  const updateReportStatus = async (reportId, status) => {
    try {
      await updateDoc(doc(db, "resourceReports", reportId), {
        status,
        reviewedAt: serverTimestamp(),
      });
      await loadReports();
    } catch (error) {
      console.error("Unable to update report status:", error);
      alert("The report status could not be updated. Please try again.");
    }
  };

  const filteredReports = reports.filter(
    (report) => reportFilter === "all" || report.status === reportFilter
  );

  const formatReportDate = (value) => {
    if (!value?.toDate) return "Pending timestamp";
    return value.toDate().toLocaleString();
  };

  const syncEditorContent = () => {
    if (!contentRef.current) return;
    setCurrentArticle((prev) => ({
      ...prev,
      content: contentRef.current.innerHTML,
    }));
  };

  const editorHasContent = () => {
    if (!contentRef.current) return false;
    return Boolean(contentRef.current.textContent.trim()) ||
      Boolean(contentRef.current.querySelector("img, iframe, hr"));
  };

  const setDefaultParagraphs = () => {
    try {
      document.execCommand("styleWithCSS", false, true);
      document.execCommand("defaultParagraphSeparator", false, "p");
    } catch (error) {
      console.warn("Could not set paragraph defaults:", error);
    }
  };

  const saveEditorSelection = () => {
    if (!contentRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (contentRef.current.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const restoreEditorSelection = () => {
    contentRef.current?.focus();
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    if (savedSelectionRef.current) {
      selection.addRange(savedSelectionRef.current);
    }
  };

  const execCommand = (command, value = null) => {
    if (contentRef.current) {
      restoreEditorSelection();
      setDefaultParagraphs();

      try {
        const success = document.execCommand(command, false, value);
        if (!success) {
          console.warn(`execCommand '${command}' failed`);
        }
        syncEditorContent();
        saveEditorSelection();
      } catch (error) {
        console.error(`Error executing command '${command}':`, error);
      }
    }
  };

  const insertHTMLAtCursor = (html) => {
    if (contentRef.current) {
      restoreEditorSelection();

      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const fragment = range.createContextualFragment(html);
        range.deleteContents();
        range.insertNode(fragment);

        // Move cursor to end of inserted content
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // If no selection, append to end
        const range = document.createRange();
        range.selectNodeContents(contentRef.current);
        range.collapse(false);
        const fragment = range.createContextualFragment(html);
        range.insertNode(fragment);

        selection.removeAllRanges();
        selection.addRange(range);
      }

      syncEditorContent();
      saveEditorSelection();
    }
  };

  const formatBlock = (tag) => {
    execCommand("formatBlock", tag);
  };

  const changeFontSize = (size) => {
    if (contentRef.current) {
      restoreEditorSelection();
      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        const selectedText = selection.toString();
        const span = `<span style="font-size: ${size};">${selectedText}</span>`;
        range.deleteContents();
        const fragment = range.createContextualFragment(span);
        range.insertNode(fragment);
        selection.removeAllRanges();
        syncEditorContent();
      }
    }
  };

  const insertList = (ordered = false) => {
    execCommand(ordered ? "insertOrderedList" : "insertUnorderedList");
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const checkSlugUniqueness = async (slug, currentArticleId = null) => {
    try {
      const slugQuery = query(
        collection(db, "articles"),
        where("slug", "==", slug)
      );
      const snapshot = await getDocs(slugQuery);

      if (snapshot.empty) return true;

      if (currentArticleId) {
        const foundDoc = snapshot.docs[0];
        return foundDoc.id === currentArticleId;
      }

      return false;
    } catch (error) {
      console.error("Error checking slug uniqueness:", error);
      return false;
    }
  };

  const cleanPastedContent = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const elementsWithStyle = tempDiv.querySelectorAll("[style]");
    elementsWithStyle.forEach((el) => {
      if (!el.closest('figure[data-image-layout]')) {
        el.removeAttribute("style");
      }
    });

    const unwantedSelectors = [
      "meta",
      "link",
      "style",
      "script",
      "o\\:p",
      "xml",
      '[class*="Mso"]',
      '[class*="mso"]',
    ];

    unwantedSelectors.forEach((selector) => {
      try {
        const elements = tempDiv.querySelectorAll(selector);
        elements.forEach((el) => el.remove());
      } catch (error) {
        console.warn(`Selector ${selector} failed, using alternative method`);
      }
    });

    const wordNamespaceElements = tempDiv.querySelectorAll("*");
    wordNamespaceElements.forEach((el) => {
      const tagName = el.tagName.toLowerCase();
      if (
        tagName.startsWith("o:") ||
        tagName.startsWith("v:") ||
        tagName.startsWith("w:") ||
        tagName.startsWith("m:")
      ) {
        el.remove();
      }
    });

    const elementsWithClass = tempDiv.querySelectorAll("[class]");
    elementsWithClass.forEach((el) => {
      const className = el.getAttribute("class");
      if (
        className &&
        (className.includes("Mso") || className.includes("mso"))
      ) {
        el.removeAttribute("class");
      }
    });

    tempDiv.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const attrName = attr.name.toLowerCase();
        if (attrName.startsWith("on")) {
          el.removeAttribute(attr.name);
        }
      });
    });

    let cleanedHTML = tempDiv.innerHTML;

    cleanedHTML = cleanedHTML.replace(/<\/?o:p[^>]*>/gi, "");
    cleanedHTML = cleanedHTML.replace(/<\/?v:[^>]*>/gi, "");
    cleanedHTML = cleanedHTML.replace(/<\/?w:[^>]*>/gi, "");
    cleanedHTML = cleanedHTML.replace(/<\/?m:[^>]*>/gi, "");

    cleanedHTML = cleanedHTML.replace(/<p[^>]*>\s*<\/p>/gi, "");
    cleanedHTML = cleanedHTML.replace(/<span[^>]*>\s*<\/span>/gi, "");

    cleanedHTML = cleanedHTML.replace(/\s*mso-[^=]*="[^"]*"/gi, "");
    cleanedHTML = cleanedHTML.replace(/\s*class="[^"]*Mso[^"]*"/gi, "");

    return cleanedHTML;
  };

  const escapeHtml = (value) => {
    const div = document.createElement("div");
    div.textContent = value;
    return div.innerHTML;
  };

  const normalizeArticleHtml = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    tempDiv.querySelectorAll("script, style, meta, link").forEach((el) => el.remove());
    tempDiv.querySelectorAll(".content-image-selected").forEach((el) => {
      el.classList.remove("content-image-selected");
    });
    tempDiv.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const attrName = attr.name.toLowerCase();
        const attrValue = attr.value || "";
        if (attrName.startsWith("on")) {
          el.removeAttribute(attr.name);
        }
        if (
          (attrName === "href" || attrName === "src") &&
          /^\s*javascript:/i.test(attrValue)
        ) {
          el.removeAttribute(attr.name);
        }
      });
    });

    Array.from(tempDiv.childNodes).forEach((node) => {
      if (node.nodeType !== Node.TEXT_NODE) return;
      const text = node.textContent.replace(/\u00a0/g, " ").trim();
      if (!text) {
        node.remove();
        return;
      }

      const paragraph = document.createElement("p");
      paragraph.textContent = text;
      node.replaceWith(paragraph);
    });

    tempDiv.querySelectorAll("p, div").forEach((el) => {
      const hasMedia = el.querySelector("img, iframe, video, hr");
      const text = el.textContent.replace(/\u00a0/g, " ").trim();
      if (!hasMedia && !text && !el.innerHTML.trim()) {
        el.innerHTML = "<br>";
      }
    });

    return tempDiv.innerHTML.trim();
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedData =
      clipboardData.getData("text/html") || clipboardData.getData("text/plain");

    if (pastedData) {
      let cleanedContent;

      if (clipboardData.getData("text/html")) {
        cleanedContent = cleanPastedContent(pastedData);
      } else {
        cleanedContent = pastedData
          .split(/\n\s*\n/)
          .map((paragraph) => paragraph.trim())
          .filter(Boolean)
          .map((paragraph) =>
            `<p>${escapeHtml(paragraph).replace(/\n/g, "<br>")}</p>`
          )
          .join("");
      }

      insertHTMLAtCursor(cleanedContent);
    }
  };

  const clampImageWidth = (width) => {
    const numericWidth = Number(width);
    if (Number.isNaN(numericWidth)) return null;
    return Math.min(100, Math.max(20, numericWidth));
  };

  const clampImageHeight = (height) => {
    const numericHeight = Number(height);
    if (Number.isNaN(numericHeight)) return null;
    return Math.min(700, Math.max(80, Math.round(numericHeight)));
  };

  const getPresetImageWidth = (layout, size) => {
    const centeredWidths = {
      small: 45,
      medium: 65,
      large: 82,
      full: 100,
    };

    const floatWidths = {
      small: 30,
      medium: 42,
      large: 52,
      full: 60,
    };

    const inlineWidths = {
      small: 30,
      medium: 45,
      large: 48,
      full: 48,
    };

    if (layout === "inline") return inlineWidths[size] || inlineWidths.medium;
    if (layout === "left" || layout === "right") {
      return floatWidths[size] || floatWidths.medium;
    }
    return centeredWidths[size] || centeredWidths.medium;
  };

  const getImageFigureStyles = (
    layout,
    size,
    customWidth = null,
    customHeight = null,
    options = {}
  ) => {
    const baseFigureStyle = "margin: 1.5rem 0;";
    const height = clampImageHeight(customHeight);
    const imgStyle = height
      ? `display: block; width: 100%; max-width: 100%; height: ${height}px; object-fit: contain; border-radius: 4px;`
      : "display: block; width: 100%; max-width: 100%; height: auto; border-radius: 4px;";

    const floatWidths = {
      small: { width: "30%", maxWidth: "260px", minWidth: "170px" },
      medium: { width: "42%", maxWidth: "340px", minWidth: "220px" },
      large: { width: "52%", maxWidth: "440px", minWidth: "240px" },
      full: { width: "60%", maxWidth: "520px", minWidth: "260px" },
    };

    const normalizedSize = ["small", "medium", "large", "full"].includes(size)
      ? size
      : "medium";
    const floatSize = floatWidths[normalizedSize];
    const widthPercent = clampImageWidth(customWidth) ||
      getPresetImageWidth(layout, normalizedSize);
    const inlineWidth = options.preserveInlineWidth
      ? widthPercent
      : Math.min(widthPercent, 48);
    const inlineMaxWidth = options.preserveInlineWidth
      ? "none"
      : "calc(50% - 0.8rem)";
    const centerWidth = Math.min(widthPercent, 100);
    const floatWidth = Math.min(widthPercent, 60);

    const layoutStyles = {
      full: {
        figure: `${baseFigureStyle} clear: both;`,
        img: imgStyle,
      },
      center: {
        figure: `${baseFigureStyle} clear: both; width: ${centerWidth}%; max-width: 100%; margin-left: auto; margin-right: auto;`,
        img: imgStyle,
      },
      left: {
        figure:
          `float: left; width: ${floatWidth}%; max-width: ${floatSize.maxWidth}; min-width: ${floatSize.minWidth}; margin: 0.35rem 1.5rem 1rem 0;`,
        img: imgStyle,
      },
      right: {
        figure:
          `float: right; width: ${floatWidth}%; max-width: ${floatSize.maxWidth}; min-width: ${floatSize.minWidth}; margin: 0.35rem 0 1rem 1.5rem;`,
        img: imgStyle,
      },
      inline: {
        figure: `display: inline-block; width: ${inlineWidth}%; max-width: ${inlineMaxWidth}; min-width: 180px; vertical-align: top; margin: 0.5rem 0.75rem 1rem 0;`,
        img: imgStyle,
      },
    };

    return layoutStyles[layout] || layoutStyles.center;
  };

  const applyImageFigureStyle = (
    figure,
    layout,
    size,
    customWidth = null,
    customHeight = null,
    options = {}
  ) => {
    if (!figure) return;
    const width = clampImageWidth(customWidth) || getPresetImageWidth(layout, size);
    const height = clampImageHeight(customHeight);
    const styles = getImageFigureStyles(layout, size, width, height, options);
    figure.dataset.imageLayout = layout;
    figure.dataset.imageSize = size;
    figure.dataset.imageWidth = String(width);
    if (height) {
      figure.dataset.imageHeight = String(height);
    } else {
      delete figure.dataset.imageHeight;
    }
    figure.style.cssText = styles.figure;
    const image = figure.querySelector("img");
    if (image) {
      image.style.cssText = styles.img;
    }

    if (layout === "inline") {
      [figure.previousElementSibling, figure.nextElementSibling].forEach((sibling) => {
        if (!sibling?.matches("p, div")) return;
        const hasContent = sibling.textContent.replace(/\u00a0/g, " ").trim();
        const hasMedia = sibling.querySelector("img, iframe, video, hr");
        if (!hasContent && !hasMedia) sibling.remove();
      });
    }
  };

  const buildImageFigureHtml = (
    imageUrl,
    layout,
    size,
    customWidth = null,
    customHeight = null,
    altText = ""
  ) => {
    const width = clampImageWidth(customWidth) || getPresetImageWidth(layout, size);
    const height = clampImageHeight(customHeight);
    const styles = getImageFigureStyles(layout, size, width, height);
    const captionStyle =
      "color: #9ca3af; font-size: 0.9rem; line-height: 1.4; margin-top: 0.5rem; text-align: center;";

    const trailingHtml = layout === "inline" ? "" : "<p><br></p>";

    return `
      <figure data-image-layout="${layout}" data-image-size="${size}" data-image-width="${width}"${height ? ` data-image-height="${height}"` : ""} style="${styles.figure}">
        <img src="${imageUrl}" alt="${escapeHtml(altText)}" loading="lazy" decoding="async" style="${styles.img}" />
        <figcaption style="${captionStyle}">Add image caption or delete this line.</figcaption>
      </figure>
      ${trailingHtml}
    `;
  };

  const getFigureFromNode = (node) => {
    const element = node?.nodeType === Node.ELEMENT_NODE
      ? node
      : node?.parentElement;
    return element?.closest?.("figure[data-image-layout]") || null;
  };

  const selectContentImage = (figure) => {
    selectedContentImageRef.current?.classList.remove("content-image-selected");
    selectedContentImageRef.current = figure;
    if (!figure) {
      setHasSelectedContentImage(false);
      setContentImageAlt("");
      return;
    }

    figure.classList.add("content-image-selected");
    setContentImageLayout(figure.dataset.imageLayout || "center");
    setContentImageSize(figure.dataset.imageSize || "medium");
    setContentImageWidth(
      clampImageWidth(figure.dataset.imageWidth) ||
        getPresetImageWidth(
          figure.dataset.imageLayout || "center",
          figure.dataset.imageSize || "medium"
        )
    );
    setContentImageHeight(
      clampImageHeight(figure.dataset.imageHeight) ||
        Math.round(figure.querySelector("img")?.getBoundingClientRect().height || 0) ||
        null
    );
    setContentImageAlt(figure.querySelector("img")?.getAttribute("alt") || "");
    setHasSelectedContentImage(true);
  };

  const updateSelectedContentImageAlt = (altText) => {
    setContentImageAlt(altText);
    const image = selectedContentImageRef.current?.querySelector("img");
    if (image && contentRef.current?.contains(image)) {
      image.setAttribute("alt", altText);
      syncEditorContent();
    }
  };

  const handleEditorClick = (e) => {
    const figure = getFigureFromNode(e.target);
    if (figure && contentRef.current?.contains(figure)) {
      selectContentImage(figure);
      return;
    }

    selectContentImage(null);
    saveEditorSelection();
  };

  const updateSelectedContentImageLayout = (layout) => {
    setContentImageLayout(layout);
    const nextWidth = getPresetImageWidth(layout, contentImageSize);
    setContentImageWidth(nextWidth);
    const figure = selectedContentImageRef.current;
    if (figure && contentRef.current?.contains(figure)) {
      applyImageFigureStyle(
        figure,
        layout,
        contentImageSize,
        nextWidth,
        contentImageHeight
      );
      syncEditorContent();
    }
  };

  const updateSelectedContentImageSize = (size) => {
    setContentImageSize(size);
    const nextWidth = getPresetImageWidth(contentImageLayout, size);
    setContentImageWidth(nextWidth);
    const figure = selectedContentImageRef.current;
    if (figure && contentRef.current?.contains(figure)) {
      applyImageFigureStyle(
        figure,
        contentImageLayout,
        size,
        nextWidth,
        contentImageHeight
      );
      syncEditorContent();
    }
  };

  const resizeSelectedContentImage = (delta) => {
    const figure = selectedContentImageRef.current;
    const nextWidth = clampImageWidth(contentImageWidth + delta);
    if (!nextWidth) return;
    setContentImageWidth(nextWidth);
    if (figure && contentRef.current?.contains(figure)) {
      applyImageFigureStyle(
        figure,
        contentImageLayout,
        contentImageSize,
        nextWidth,
        contentImageHeight
      );
      syncEditorContent();
    }
  };

  const getImageRowFigures = (figure) => {
    if (!figure) return [];
    const row = figure.closest('[data-image-row="true"]');
    if (row) {
      return Array.from(row.querySelectorAll('figure[data-image-layout="inline"]'));
    }

    const figures = [figure];
    let previous = figure.previousElementSibling;
    while (previous) {
      if (previous.matches('figure[data-image-layout="inline"]')) {
        figures.unshift(previous);
        previous = previous.previousElementSibling;
        continue;
      }
      if (previous.matches("p, div") && !previous.textContent.replace(/\u00a0/g, " ").trim()) {
        previous = previous.previousElementSibling;
        continue;
      }
      break;
    }

    let next = figure.nextElementSibling;
    while (next) {
      if (next.matches('figure[data-image-layout="inline"]')) {
        figures.push(next);
        next = next.nextElementSibling;
        continue;
      }
      if (next.matches("p, div") && !next.textContent.replace(/\u00a0/g, " ").trim()) {
        next = next.nextElementSibling;
        continue;
      }
      break;
    }

    return figures;
  };

  const resizeSelectedContentImageHeight = (delta) => {
    const figure = selectedContentImageRef.current;
    const currentHeight =
      contentImageHeight ||
      Math.round(figure?.querySelector("img")?.getBoundingClientRect().height || 0);
    const nextHeight = clampImageHeight(currentHeight + delta);
    if (!nextHeight) return;
    setContentImageHeight(nextHeight);
    if (figure && contentRef.current?.contains(figure)) {
      applyImageFigureStyle(
        figure,
        contentImageLayout,
        contentImageSize,
        contentImageWidth,
        nextHeight
      );
      syncEditorContent();
    }
  };

  const matchSelectedRowImageHeight = () => {
    const figure = selectedContentImageRef.current;
    if (!figure || !contentRef.current?.contains(figure)) return;

    const rowFigures = getImageRowFigures(figure);
    const targetHeight = clampImageHeight(
      Math.max(
        ...rowFigures.map((rowFigure) =>
          rowFigure.querySelector("img")?.getBoundingClientRect().height || 0
        )
      )
    );
    if (!targetHeight) return;

    rowFigures.forEach((rowFigure) => {
      applyImageFigureStyle(
        rowFigure,
        rowFigure.dataset.imageLayout || "inline",
        rowFigure.dataset.imageSize || "medium",
        rowFigure.dataset.imageWidth,
        targetHeight
      );
    });

    setContentImageHeight(targetHeight);
    syncEditorContent();
  };

  const centerSelectedImageOrRow = () => {
    const figure = selectedContentImageRef.current;
    if (!figure || !contentRef.current?.contains(figure)) return;

    const rowFigures = getImageRowFigures(figure);
    if ((figure.dataset.imageLayout === "inline" || rowFigures.length > 1) && rowFigures.length > 0) {
      let row = figure.closest('[data-image-row="true"]');
      if (!row) {
        row = document.createElement("div");
        row.dataset.imageRow = "true";
        row.style.cssText = "clear: both; margin: 1.5rem 0; text-align: center;";
        rowFigures[0].parentNode.insertBefore(row, rowFigures[0]);
        rowFigures.forEach((rowFigure) => row.appendChild(rowFigure));
      } else {
        row.style.cssText = "clear: both; margin: 1.5rem 0; text-align: center;";
      }

      rowFigures.forEach((rowFigure) => {
        applyImageFigureStyle(
          rowFigure,
          "inline",
          rowFigure.dataset.imageSize || "medium",
          rowFigure.dataset.imageWidth,
          rowFigure.dataset.imageHeight,
          { preserveInlineWidth: true }
        );
      });
    } else {
      updateSelectedContentImageLayout("center");
      return;
    }

    syncEditorContent();
  };

  const deleteSelectedContentImage = () => {
    const figure = selectedContentImageRef.current;
    if (!figure || !contentRef.current?.contains(figure)) return;
    const nextFocusTarget = figure.nextElementSibling || figure.previousElementSibling;
    figure.remove();
    selectContentImage(null);
    syncEditorContent();

    if (nextFocusTarget) {
      const range = document.createRange();
      range.selectNodeContents(nextFocusTarget);
      range.collapse(true);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const handleEditorKeyDown = (e) => {
    if (
      selectedContentImageRef.current &&
      (e.key === "Delete" || e.key === "Backspace")
    ) {
      const selection = window.getSelection();
      const anchorNode = selection?.anchorNode;
      const caption = selectedContentImageRef.current.querySelector("figcaption");
      if (anchorNode && caption?.contains(anchorNode)) return;

      e.preventDefault();
      deleteSelectedContentImage();
    }
  };

  const insertLink = () => {
    restoreEditorSelection();
    const selection = window.getSelection();
    const selectedText = selection?.toString();
    const url = window.prompt("Enter the link URL");
    if (!url) return;

    const href = url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;

    if (selectedText) {
      execCommand("createLink", href);
    } else {
      insertHTMLAtCursor(
        `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(href)}</a>`
      );
    }
  };

  const uploadImageToFirebase = async (file, path) => {
    const timestamp = Date.now();
    const filename = `${timestamp}-${file.name}`;
    const storageRef = ref(storage, `${path}/${filename}`);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  // Generate a cropped social share image (1200x630 - Facebook/Twitter optimal)
  // Takes a File/Blob or loads from URL via an img element
  const generateSocialImage = async (imageSource, focusX, focusY) => {
    let imageBitmap;

    if (imageSource instanceof File || imageSource instanceof Blob) {
      // Use the file directly
      imageBitmap = await createImageBitmap(imageSource);
    } else {
      // Load from URL using an img element (works with CORS)
      imageBitmap = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = imageSource;
      });
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Social media optimal size
    const targetWidth = 1200;
    const targetHeight = 630;
    const targetRatio = targetWidth / targetHeight; // ~1.9

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const imgRatio = imageBitmap.width / imageBitmap.height;

    let sourceX, sourceY, sourceWidth, sourceHeight;

    if (imgRatio > targetRatio) {
      // Image is wider than target - crop sides based on focusX
      sourceHeight = imageBitmap.height;
      sourceWidth = imageBitmap.height * targetRatio;
      const maxOffsetX = imageBitmap.width - sourceWidth;
      sourceX = (focusX / 100) * maxOffsetX;
      sourceY = 0;
    } else {
      // Image is taller than target - crop top/bottom based on focusY
      sourceWidth = imageBitmap.width;
      sourceHeight = imageBitmap.width / targetRatio;
      const maxOffsetY = imageBitmap.height - sourceHeight;
      sourceX = 0;
      sourceY = (focusY / 100) * maxOffsetY;
    }

    ctx.drawImage(
      imageBitmap,
      sourceX, sourceY, sourceWidth, sourceHeight,
      0, 0, targetWidth, targetHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob((resultBlob) => {
        if (resultBlob) {
          resolve(resultBlob);
        } else {
          reject(new Error("Failed to create image blob"));
        }
      }, "image/jpeg", 0.9);
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setSaving(true);
        const imageUrl = await uploadImageToFirebase(file, "images/featured");
        setCurrentArticle((prev) => ({
          ...prev,
          image: file,
          imageUrl: imageUrl,
        }));
      } catch (error) {
        console.error("Error uploading image:", error);
        alert("Error uploading image. Please try again.");
      }
      setSaving(false);
    }
  };

  const insertImageIntoContent = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setSaving(true);
        const imageUrl = await uploadImageToFirebase(file, "images/content");
        const suggestedAlt = file.name
          .replace(/\.[^.]+$/, "")
          .replace(/[-_]+/g, " ")
          .trim();
        insertHTMLAtCursor(
          buildImageFigureHtml(
            imageUrl,
            contentImageLayout,
            contentImageSize,
            contentImageWidth,
            contentImageHeight,
            suggestedAlt
          )
        );
        setSaving(false);
      } catch (error) {
        console.error("Error uploading content image:", error);
        alert("Error uploading image. Please try again.");
        setSaving(false);
      }
      e.target.value = "";
    }
  };

  const handleColorChange = (color) => {
    setCurrentColor(color);
    execCommand("foreColor", color);
    setShowColorPicker(false);
  };

  const predefinedColors = [
    "#ffffff",
    "#000000",
    "#ff0000",
    "#00ff00",
    "#0000ff",
    "#ffff00",
    "#ff00ff",
    "#00ffff",
    "#ffa500",
    "#800080",
    "#008000",
    "#800000",
    "#000080",
    "#808080",
    "#c0c0c0",
    "#ff6b6b",
    "#4ecdc4",
    "#45b7d1",
    "#f9ca24",
    "#6c5ce7",
    "#fd79a8",
    "#fdcb6e",
    "#55a3ff",
    "#00b894",
    "#e17055",
    "#00d8d8",
  ];

  const cleanCurrentContent = () => {
    if (contentRef.current) {
      const currentHTML = contentRef.current.innerHTML;
      const cleanedHTML = normalizeArticleHtml(cleanPastedContent(currentHTML));
      contentRef.current.innerHTML = cleanedHTML;
      syncEditorContent();
    }
  };

  const saveArticle = async () => {
    if (!currentArticle.title || !editorHasContent()) {
      alert("Please fill in title and content");
      return;
    }

    if (!currentArticle.slug) {
      alert("Please enter a URL slug for this article");
      return;
    }

    if (!currentArticle.excerpt.trim()) {
      alert("Please add an excerpt for article previews and search results");
      return;
    }

    if (!currentArticle.date) {
      alert("Please select a publication date");
      return;
    }

    const normalizedSlug = generateSlug(currentArticle.slug);
    const isUnique = await checkSlugUniqueness(normalizedSlug, editingId);
    if (!isUnique) {
      alert(
        `The slug "${normalizedSlug}" is already in use. Please choose a different one.`
      );
      return;
    }

    setSaving(true);
    try {
      const content = normalizeArticleHtml(contentRef.current.innerHTML);
      contentRef.current.innerHTML = content;

      // Generate cropped social image if we have a featured image
      let socialImageUrl = currentArticle.socialImageUrl || "";
      if (currentArticle.imageUrl) {
        // Use the original file if available (newly uploaded), otherwise we can't crop
        const imageSource = currentArticle.image; // This is the File object from upload

        if (imageSource) {
          try {
            console.log("Generating social image with focus:", {
              focusX: currentArticle.focusX,
              focusY: currentArticle.focusY,
            });
            const socialBlob = await generateSocialImage(
              imageSource,
              currentArticle.focusX ?? 50,
              currentArticle.focusY ?? 50
            );
            console.log("Social image blob created:", socialBlob.size, "bytes");
            const socialFile = new File([socialBlob], `social-${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            socialImageUrl = await uploadImageToFirebase(socialFile, "images/social");
            console.log("Social image uploaded:", socialImageUrl);
          } catch (err) {
            console.error("Could not generate social image:", err);
            socialImageUrl = currentArticle.imageUrl;
          }
        } else {
          // No file available (editing existing article without re-uploading image)
          // Keep existing socialImageUrl or fall back to imageUrl
          console.log("No image file available for cropping, using existing image");
          if (!socialImageUrl) {
            socialImageUrl = currentArticle.imageUrl;
          }
        }
      }

      const savedAt = new Date().toISOString();
      const articleData = {
        title: currentArticle.title.trim(),
        slug: normalizedSlug,
        category: currentArticle.category,
        author: currentArticle.author,
        excerpt: currentArticle.excerpt.trim(),
        content: content,
        imageUrl: currentArticle.imageUrl || "",
        socialImageUrl: socialImageUrl,
        focusX: currentArticle.focusX ?? 50,
        focusY: currentArticle.focusY ?? 50,
        featured: currentArticle.featured,
        opinionArticle: currentArticle.opinionArticle,
        date: currentArticle.date,
        readTime:
          Math.max(
            1,
            Math.ceil(
              content
                .replace(/<[^>]*>/g, " ")
                .trim()
                .split(/\s+/)
                .filter(Boolean).length / 200
            )
          ) +
          " min read",
        updatedAt: savedAt,
        dateModified: isArticlePublished(currentArticle)
          ? savedAt
          : currentArticle.date,
      };

      if (editingId) {
        await updateDoc(doc(db, "articles", editingId), articleData);
      } else {
        articleData.createdAt = savedAt;
        await addDoc(collection(db, "articles"), articleData);
      }

      await loadArticles();
      resetForm();
      const scheduled = !isArticlePublished(articleData);
      alert(
        scheduled
          ? `Article scheduled for ${articleData.date}. It will remain hidden until that date in New Hampshire.`
          : editingId
          ? "Article updated successfully!"
          : "Article published successfully!"
      );
    } catch (error) {
      console.error("Error saving article:", error);
      alert("Error saving article. Please try again.");
    }
    setSaving(false);
  };

  const editArticle = (article) => {
    setCurrentArticle({
      title: article.title,
      slug: article.slug,
      category: article.category,
      author: article.author,
      excerpt: article.excerpt,
      content: article.content,
      image: null,
      imageUrl: article.imageUrl || "",
      focusX: article.focusX ?? 50,
      focusY: article.focusY ?? 50,
      socialImageUrl: article.socialImageUrl || "",
      featured: article.featured,
      opinionArticle: article.opinionArticle || false,
      date: article.date,
    });

    setEditingId(article.id);
    setShowEditor(true);
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.innerHTML = article.content || "<p><br></p>";
      }
    }, 100);
  };

  const deleteArticle = async (id) => {
    if (window.confirm("Are you sure you want to delete this article?")) {
      try {
        await deleteDoc(doc(db, "articles", id));
        await loadArticles();
        alert("Article deleted successfully!");
      } catch (error) {
        console.error("Error deleting article:", error);
        alert("Error deleting article. Please try again.");
      }
    }
  };

  const resetForm = () => {
    setCurrentArticle({
      title: "",
      slug: "",
      category: "blog-posts",
      author: "",
      excerpt: "",
      content: "",
      image: null,
      imageUrl: "",
      focusX: 50,
      socialImageUrl: "",
      focusY: 50,
      featured: false,
      opinionArticle: false,
      date: getPublicationDate(),
    });

    setEditingId(null);
    setShowEditor(false);
    if (contentRef.current) {
      contentRef.current.innerHTML = "";
    }
  };

  const cancelEdit = () => {
    resetForm();
  };

  // CONDITIONAL LOGIC COMES LAST - after all hooks and functions
  if (authChecking) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-serif">
        <SEO title="Admin Dashboard" noindex={true} />
        <Nav />
        <div className="flex min-h-[80vh] items-center justify-center px-4">
          <p className="text-gray-300" role="status">Checking secure admin access…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-serif">
        <SEO title="Admin Dashboard" noindex={true} />
        <Nav />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="bg-gray-800 border-2 border-cyan-500 p-8 max-w-md w-full mx-4">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-cyan-400 mb-2">
                ADMIN ACCESS
              </h1>
              <p className="text-gray-300">
                Sign in with a one-time secure link sent to the authorized administrator email.
              </p>
            </div>

            <button
              type="button"
              onClick={handleSendLoginLink}
              className="w-full bg-cyan-500 text-gray-900 py-3 font-semibold hover:bg-cyan-400 transition-colors"
            >
              EMAIL ME A SECURE SIGN-IN LINK
            </button>

            {loginMessage && (
              <p className="mt-4 rounded border border-green-700 bg-green-950 p-3 text-sm text-green-200" role="status">
                {loginMessage}
              </p>
            )}
            {loginError && (
              <p className="mt-4 rounded border border-red-700 bg-red-950 p-3 text-sm text-red-200" role="alert">
                {loginError}
              </p>
            )}

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">Authorized personnel only</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-serif">
      <SEO title="Admin Dashboard" noindex={true} />
      <Nav />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold text-cyan-400 mb-4">
                ADMIN DASHBOARD
              </h1>
              <p className="text-gray-300 mb-6">
                Manage articles and blog posts with Firebase
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-4 py-2 font-semibold hover:bg-red-500 transition-colors"
            >
              LOGOUT
            </button>
          </div>

          {!showEditor && (
            <button
              onClick={() => setShowEditor(true)}
              className="bg-cyan-500 text-gray-900 px-6 py-3 font-semibold hover:bg-cyan-400 transition-colors"
              disabled={loading}
            >
              CREATE NEW ARTICLE
            </button>
          )}
        </div>

        <section className="mb-8 border border-gray-600 bg-gray-800 p-6" aria-labelledby="resource-reports-heading">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 id="resource-reports-heading" className="text-2xl font-bold text-cyan-400">
                RESOURCE INFORMATION REPORTS
              </h2>
              <p className="mt-1 text-sm text-gray-400">
                {reports.filter((report) => report.status === "open").length} open report(s)
              </p>
            </div>
            <div className="flex flex-wrap gap-2" aria-label="Filter resource reports">
              {["open", "resolved", "dismissed", "all"].map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setReportFilter(status)}
                  className={`rounded px-3 py-2 text-sm font-semibold capitalize transition-colors ${
                    reportFilter === status
                      ? "bg-cyan-500 text-gray-900"
                      : "bg-gray-700 text-gray-200 hover:bg-gray-600"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {reportsLoading ? (
            <p className="mt-5 text-gray-300" role="status">Loading reports…</p>
          ) : filteredReports.length === 0 ? (
            <p className="mt-5 rounded border border-gray-700 bg-gray-900 p-4 text-gray-400">
              No {reportFilter === "all" ? "" : `${reportFilter} `}reports.
            </p>
          ) : (
            <div className="mt-5 space-y-4">
              {filteredReports.map((report) => (
                <article key={report.id} className="rounded border border-gray-600 bg-gray-900 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white">{report.resourceName}</h3>
                      <p className="text-xs text-gray-400">
                        {report.directory === "veteran" ? "Veteran directory" : "General directory"}
                        {" · "}Resource ID {report.resourceId}
                        {" · "}{formatReportDate(report.createdAt)}
                      </p>
                    </div>
                    <span className={`w-fit rounded px-2 py-1 text-xs font-bold uppercase ${
                      report.status === "open"
                        ? "bg-yellow-500 text-gray-950"
                        : report.status === "resolved"
                          ? "bg-green-700 text-white"
                          : "bg-gray-600 text-white"
                    }`}>
                      {report.status}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {(report.reasons || []).map((reason) => (
                      <span key={reason} className="rounded bg-gray-700 px-2 py-1 text-xs text-cyan-200">
                        {REPORT_REASON_LABELS[reason] || reason}
                      </span>
                    ))}
                  </div>

                  {report.details && (
                    <p className="mt-3 whitespace-pre-wrap rounded bg-gray-800 p-3 text-sm text-gray-200">
                      {report.details}
                    </p>
                  )}

                  <dl className="mt-3 grid gap-2 text-xs text-gray-400 md:grid-cols-2">
                    {report.snapshot?.address && <div><dt className="font-semibold text-gray-300">Reported address snapshot</dt><dd>{report.snapshot.address}</dd></div>}
                    {report.snapshot?.phone && <div><dt className="font-semibold text-gray-300">Reported phone snapshot</dt><dd>{report.snapshot.phone}</dd></div>}
                    {report.snapshot?.website && <div><dt className="font-semibold text-gray-300">Reported website snapshot</dt><dd className="break-all">{report.snapshot.website}</dd></div>}
                    {report.snapshot?.email && <div><dt className="font-semibold text-gray-300">Reported email snapshot</dt><dd className="break-all">{report.snapshot.email}</dd></div>}
                  </dl>

                  <p className="mt-3 text-xs text-gray-500">Page: {report.pagePath}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {report.status !== "resolved" && (
                      <button
                        type="button"
                        onClick={() => updateReportStatus(report.id, "resolved")}
                        className="rounded bg-green-700 px-3 py-2 text-sm font-semibold text-white hover:bg-green-600"
                      >
                        Mark resolved
                      </button>
                    )}
                    {report.status !== "dismissed" && (
                      <button
                        type="button"
                        onClick={() => updateReportStatus(report.id, "dismissed")}
                        className="rounded bg-gray-600 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-500"
                      >
                        Dismiss
                      </button>
                    )}
                    {report.status !== "open" && (
                      <button
                        type="button"
                        onClick={() => updateReportStatus(report.id, "open")}
                        className="rounded bg-yellow-500 px-3 py-2 text-sm font-semibold text-gray-950 hover:bg-yellow-400"
                      >
                        Reopen
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {(loading || saving) && (
          <div className="bg-blue-600 text-white p-4 mb-6 rounded">
            {loading ? "Loading articles..." : "Saving..."}
          </div>
        )}

        {showEditor && (
          <div className="bg-gray-800 border border-gray-600 p-6 mb-8">
            <h2 className="text-2xl font-bold text-cyan-400 mb-6">
              {editingId ? "EDIT ARTICLE" : "CREATE ARTICLE"}
            </h2>

            {/* SEO Tips Panel */}
            <details className="bg-gray-900 border border-cyan-700 rounded mb-6">
              <summary className="cursor-pointer px-4 py-3 text-cyan-400 font-semibold text-sm hover:bg-gray-800 select-none">
                SEO CHECKLIST &mdash; Click to expand tips for better search rankings
              </summary>
              <div className="px-4 pb-4 pt-2 text-sm text-gray-300 space-y-3">
                <div className="border-b border-gray-700 pb-3">
                  <p className="font-semibold text-white mb-1">Title</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Write a concise, accurate title that clearly identifies the specific topic</li>
                    <li>Use the words a reader would search for when they fit naturally; do not force or repeat keywords</li>
                    <li>Aim for about 60 characters, but prioritize clarity over an exact character count</li>
                  </ul>
                </div>
                <div className="border-b border-gray-700 pb-3">
                  <p className="font-semibold text-white mb-1">URL Slug</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Use lowercase, hyphens between words: <span className="text-cyan-400">recovery-resources-laconia-nh</span></li>
                    <li>Include your target keyword in the slug</li>
                    <li>Keep it short, descriptive, and stable after publication</li>
                  </ul>
                </div>
                <div className="border-b border-gray-700 pb-3">
                  <p className="font-semibold text-white mb-1">Excerpt (Meta Description)</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Summarize the page accurately; Google may use this text as the search snippet</li>
                    <li>Aim for roughly 120-160 characters so the important message is likely to fit</li>
                    <li>Include New Hampshire or a city/county only when the article genuinely focuses there</li>
                    <li>Give each article a unique excerpt rather than reusing a template</li>
                  </ul>
                </div>
                <div className="border-b border-gray-700 pb-3">
                  <p className="font-semibold text-white mb-1">Helpful, Trustworthy Content</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Answer the reader's question completely; Google has no preferred word count</li>
                    <li>Use descriptive H2 and H3 headings, short paragraphs, and lists where they improve scanning</li>
                    <li>Add original reporting, first-hand experience, or analysis instead of only summarizing other pages</li>
                    <li>For treatment, mental health, safety, or medical claims, cite current primary sources and have a qualified person review the article</li>
                    <li>Link to relevant Recovery Compass resources and reputable sources using descriptive link text</li>
                  </ul>
                </div>
                <div className="border-b border-gray-700 pb-3">
                  <p className="font-semibold text-white mb-1">Author and Transparency</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Use the real writer's name and make their relevant experience clear in the article or About page</li>
                    <li>Distinguish personal experience and opinion from medical guidance</li>
                    <li>Explain reporting methods, interviews, and AI assistance when readers would reasonably want to know</li>
                  </ul>
                </div>
                <div className="border-b border-gray-700 pb-3">
                  <p className="font-semibold text-white mb-1">Images</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Always upload a featured image &mdash; articles with images get more clicks</li>
                    <li>The editor creates a 1200x630 social image from the featured image</li>
                    <li>Use descriptive filenames before uploading: <span className="text-cyan-400">laconia-recovery-center.jpg</span> not <span className="text-red-400">IMG_4523.jpg</span></li>
                    <li>Add a useful caption and descriptive alt text to content images; do not stuff keywords into alt text</li>
                  </ul>
                </div>
                <div>
                  <p className="font-semibold text-white mb-1">After Publishing</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-400">
                    <li>Published articles are automatically added to the sitemap; scheduled articles stay hidden until their publication date</li>
                    <li>Share on Facebook/social media &mdash; the OG tags will pull in the title, excerpt, and social image automatically</li>
                    <li>Go to <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">Google Search Console</a> and use "URL Inspection" to request indexing for new articles</li>
                    <li>Update an article when its facts or recommendations change; do not change dates only to make content look fresh</li>
                  </ul>
                </div>
              </div>
            </details>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  TITLE
                </label>
                <input
                  type="text"
                  value={currentArticle.title}
                  onChange={(e) =>
                    setCurrentArticle((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  className="w-full bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  placeholder="Enter article title..."
                />
                <p className={`text-xs mt-1 ${
                  currentArticle.title.length === 0
                    ? "text-gray-500"
                    : currentArticle.title.length <= 60
                    ? "text-green-400"
                    : "text-yellow-400"
                }`}>
                  {currentArticle.title.length}/60 characters
                  {currentArticle.title.length > 60 && " — may get cut off in search results"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  URL SLUG
                </label>
                <input
                  type="text"
                  value={currentArticle.slug}
                  onChange={(e) =>
                    setCurrentArticle((prev) => ({
                      ...prev,
                      slug: e.target.value,
                    }))
                  }
                  className="w-full bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  placeholder="url-friendly-slug"
                />
                <p className="text-xs text-gray-400 mt-1">
                  URL will be: /articles/
                  {currentArticle.slug || "your-slug-here"}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  CATEGORY
                </label>
                <select
                  value={currentArticle.category}
                  onChange={(e) =>
                    setCurrentArticle((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  className="w-full bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  AUTHOR
                </label>
                <input
                  type="text"
                  value={currentArticle.author}
                  onChange={(e) =>
                    setCurrentArticle((prev) => ({
                      ...prev,
                      author: e.target.value,
                    }))
                  }
                  className="w-full bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                  placeholder="Author name (optional)..."
                />
                <p className="text-xs text-gray-400 mt-1">
                  Use the real writer's name. If blank, Recovery Compass NH is credited.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  PUBLICATION DATE
                </label>
                <input
                  type="date"
                  value={currentArticle.date}
                  onChange={(e) =>
                    setCurrentArticle((prev) => ({
                      ...prev,
                      date: e.target.value,
                    }))
                  }
                  className="w-full bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {isArticlePublished(currentArticle)
                    ? "Publishes immediately."
                    : `Scheduled: hidden until ${currentArticle.date} in New Hampshire.`}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  FEATURED ARTICLE
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={currentArticle.featured}
                    onChange={(e) =>
                      setCurrentArticle((prev) => ({
                        ...prev,
                        featured: e.target.checked,
                      }))
                    }
                    className="mr-2"
                  />
                  <span className="text-gray-300">Mark as featured</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  OPINION ARTICLE
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={currentArticle.opinionArticle}
                    onChange={(e) =>
                      setCurrentArticle((prev) => ({
                        ...prev,
                        opinionArticle: e.target.checked,
                      }))
                    }
                    className="mr-2"
                  />
                  <span className="text-gray-300">Mark as opinion piece</span>
                </label>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                EXCERPT{" "}
                <span className="text-xs font-normal text-gray-500">
                  (used as meta description in Google search results)
                </span>
              </label>
              <textarea
                value={currentArticle.excerpt}
                onChange={(e) =>
                  setCurrentArticle((prev) => ({
                    ...prev,
                    excerpt: e.target.value,
                  }))
                }
                className="w-full bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:border-cyan-500 focus:outline-none h-24 resize-none"
                placeholder="Brief excerpt for article preview... (aim for 120-160 characters for best SEO)"
              />
              <p className={`text-xs mt-1 ${
                currentArticle.excerpt.length === 0
                  ? "text-gray-500"
                  : currentArticle.excerpt.length < 120
                  ? "text-yellow-400"
                  : currentArticle.excerpt.length <= 160
                  ? "text-green-400"
                  : "text-red-400"
              }`}>
                {currentArticle.excerpt.length}/160 characters
                {currentArticle.excerpt.length > 0 && currentArticle.excerpt.length < 120 && " — consider adding specific detail"}
                {currentArticle.excerpt.length >= 120 && currentArticle.excerpt.length <= 160 && " — good working range"}
                {currentArticle.excerpt.length > 160 && " — put the most important information earlier"}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                FEATURED IMAGE
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="w-full bg-gray-700 border border-gray-600 px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                disabled={saving}
              />
              {currentArticle.imageUrl && (
                <div className="mt-2">
                  <p className="text-xs text-green-400 mb-2">
                    ✓ Image uploaded to Firebase
                  </p>

                  {/* Controls */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1">
                        IMAGE FOCUS – LEFT ↔ RIGHT ({currentArticle.focusX}%)
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={currentArticle.focusX}
                        onChange={(e) =>
                          setCurrentArticle((prev) => ({
                            ...prev,
                            focusX: Number(e.target.value),
                          }))
                        }
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 mb-1">
                        IMAGE FOCUS – TOP ↕ BOTTOM ({currentArticle.focusY}%)
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={currentArticle.focusY}
                        onChange={(e) =>
                          setCurrentArticle((prev) => ({
                            ...prev,
                            focusY: Number(e.target.value),
                          }))
                        }
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Preview of featured image with focus */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      PREVIEW (Featured Image)
                    </label>
                    <div className="w-full max-w-md h-64 border border-gray-600 bg-gray-900 overflow-hidden">
                      <ArticleImage
                        src={currentArticle.imageUrl}
                        alt="Featured image preview"
                        focusX={currentArticle.focusX}
                        focusY={currentArticle.focusY}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      This is how your featured image will appear in article cards
                    </p>
                  </div>

                  {/* Social share preview - uses the same featured image */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      SOCIAL SHARE PREVIEW (Facebook / X)
                    </label>
                    <p className="text-xs text-gray-400 mb-2">
                      This shows how your featured image will appear when shared on social media.
                    </p>
                    {/* Preview in wide FB-like frame */}
                    <div className="w-full max-w-md aspect-[1.91/1] border border-gray-600 bg-gray-900 overflow-hidden">
                      <img
                        src={currentArticle.imageUrl}
                        alt="Social preview"
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: `${currentArticle.focusX}% ${currentArticle.focusY}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Use the sliders above to adjust how your image is cropped for social sharing.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                CONTENT
              </label>

              {/* IMPROVED: Better toolbar with working buttons - STICKY */}
              <div className="sticky top-0 z-50 flex flex-wrap items-center gap-2 p-3 bg-gray-700 border border-gray-600 mb-0 shadow-lg">
                <select
                  onChange={(e) => {
                    formatBlock(e.target.value);
                    e.target.value = "p";
                  }}
                  defaultValue="p"
                  className="bg-gray-800 border border-gray-600 px-3 py-1 text-white text-sm focus:border-cyan-500 focus:outline-none"
                  disabled={saving}
                  title="Text style"
                >
                  <option value="p">Paragraph</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                  <option value="h4">Heading 4</option>
                </select>
                <button
                  type="button"
                  onClick={() => execCommand("bold")}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm font-bold"
                  disabled={saving}
                  title="Bold"
                >
                  B
                </button>
                <button
                  type="button"
                  onClick={() => execCommand("italic")}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm italic"
                  disabled={saving}
                  title="Italic"
                >
                  I
                </button>
                <button
                  type="button"
                  onClick={() => execCommand("underline")}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm underline"
                  disabled={saving}
                  title="Underline"
                >
                  U
                </button>
                <button
                  type="button"
                  onClick={insertLink}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Insert link"
                >
                  Link
                </button>

                <div className="w-px bg-gray-500"></div>

                {/* NEW: Font sizes */}
                <button
                  type="button"
                  onClick={() => changeFontSize("0.75rem")}
                  className="px-2 py-1 bg-gray-600 hover:bg-gray-500 text-white text-xs"
                  disabled={saving}
                  title="Small text"
                >
                  S
                </button>
                <button
                  type="button"
                  onClick={() => changeFontSize("1rem")}
                  className="px-2 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Medium text"
                >
                  M
                </button>
                <button
                  type="button"
                  onClick={() => changeFontSize("1.25rem")}
                  className="px-2 py-1 bg-gray-600 hover:bg-gray-500 text-white text-base"
                  disabled={saving}
                  title="Large text"
                >
                  L
                </button>

                <div className="w-px bg-gray-500"></div>

                {/* IMPROVED: Headers that actually work */}
                <button
                  type="button"
                  onClick={() => formatBlock("h1")}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Heading 1"
                >
                  H1
                </button>
                <button
                  type="button"
                  onClick={() => formatBlock("h2")}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  type="button"
                  onClick={() => formatBlock("h3")}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Heading 3"
                >
                  H3
                </button>
                <button
                  type="button"
                  onClick={() => formatBlock("p")}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Paragraph"
                >
                  P
                </button>

                <div className="w-px bg-gray-500"></div>

                {/* IMPROVED: Lists that actually work */}
                <button
                  type="button"
                  onClick={() => insertList(false)}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Bullet list"
                >
                  • List
                </button>
                <button
                  type="button"
                  onClick={() => insertList(true)}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Numbered list"
                >
                  1. List
                </button>
                <button
                  type="button"
                  onClick={() => formatBlock("blockquote")}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Quote"
                >
                  Quote
                </button>
                <div className="w-px bg-gray-500"></div>

                {/* Color Picker */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm flex items-center gap-1"
                    disabled={saving}
                    title="Text color"
                  >
                    <span
                      className="w-3 h-3 border border-gray-300 inline-block"
                      style={{ backgroundColor: currentColor }}
                    ></span>
                    Color
                  </button>

                  {showColorPicker && (
                    <div className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 p-3 z-50 shadow-lg">
                      <div className="grid grid-cols-5 gap-1 mb-3">
                        {predefinedColors.map((color) => (
                          <button
                            key={color}
                            type="button"
                            className="w-6 h-6 border border-gray-400 hover:border-white cursor-pointer"
                            style={{ backgroundColor: color }}
                            onClick={() => handleColorChange(color)}
                            title={color}
                          />
                        ))}
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={currentColor}
                          onChange={(e) => handleColorChange(e.target.value)}
                          className="w-8 h-8 border border-gray-400 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={currentColor}
                          onChange={(e) => setCurrentColor(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleColorChange(currentColor);
                            }
                          }}
                          className="bg-gray-700 border border-gray-600 px-2 py-1 text-white text-xs w-20"
                          placeholder="#ffffff"
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowColorPicker(false)}
                        className="mt-2 text-xs text-gray-400 hover:text-white"
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>

                <div className="w-px bg-gray-500"></div>

                {/* Other tools */}
                <button
                  type="button"
                  onClick={() =>
                    insertHTMLAtCursor(
                      '<hr style="margin: 1rem 0; border: 1px solid #4b5563;">'
                    )
                  }
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm"
                  disabled={saving}
                  title="Horizontal line"
                >
                  Line
                </button>
                <select
                  value={contentImageLayout}
                  onChange={(e) => updateSelectedContentImageLayout(e.target.value)}
                  className="bg-gray-800 border border-gray-600 px-3 py-1 text-white text-sm focus:border-cyan-500 focus:outline-none"
                  disabled={saving}
                  title="Image placement"
                >
                  <option value="full">Full image</option>
                  <option value="center">Centered image</option>
                  <option value="inline">Side-by-side</option>
                  <option value="left">Text wraps left</option>
                  <option value="right">Text wraps right</option>
                </select>
                <select
                  value={contentImageSize}
                  onChange={(e) => updateSelectedContentImageSize(e.target.value)}
                  className="bg-gray-800 border border-gray-600 px-3 py-1 text-white text-sm focus:border-cyan-500 focus:outline-none"
                  disabled={saving}
                  title="Image size"
                >
                  <option value="small">Small image</option>
                  <option value="medium">Medium image</option>
                  <option value="large">Large image</option>
                  <option value="full">Max image</option>
                </select>
                <div className="flex items-center bg-gray-800 border border-gray-600">
                  <button
                    type="button"
                    onClick={() => resizeSelectedContentImage(-1)}
                    className="px-2 py-1 text-white text-sm hover:bg-gray-600 disabled:opacity-40"
                    disabled={saving || !hasSelectedContentImage}
                    title="Make selected image smaller"
                  >
                    -
                  </button>
                  <span className="px-2 text-xs text-gray-300 min-w-12 text-center">
                    {contentImageWidth}%
                  </span>
                  <button
                    type="button"
                    onClick={() => resizeSelectedContentImage(1)}
                    className="px-2 py-1 text-white text-sm hover:bg-gray-600 disabled:opacity-40"
                    disabled={saving || !hasSelectedContentImage}
                    title="Make selected image larger"
                  >
                    +
                  </button>
                </div>
                <div className="flex items-center bg-gray-800 border border-gray-600">
                  <button
                    type="button"
                    onClick={() => resizeSelectedContentImageHeight(-1)}
                    className="px-2 py-1 text-white text-sm hover:bg-gray-600 disabled:opacity-40"
                    disabled={saving || !hasSelectedContentImage}
                    title="Make selected image shorter"
                  >
                    H-
                  </button>
                  <span className="px-2 text-xs text-gray-300 min-w-14 text-center">
                    {contentImageHeight ? `${contentImageHeight}px` : "auto"}
                  </span>
                  <button
                    type="button"
                    onClick={() => resizeSelectedContentImageHeight(1)}
                    className="px-2 py-1 text-white text-sm hover:bg-gray-600 disabled:opacity-40"
                    disabled={saving || !hasSelectedContentImage}
                    title="Make selected image taller"
                  >
                    H+
                  </button>
                </div>
                <button
                  type="button"
                  onClick={matchSelectedRowImageHeight}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm disabled:opacity-40"
                  disabled={saving || !hasSelectedContentImage}
                  title="Make side-by-side images the same height"
                >
                  Match height
                </button>
                <button
                  type="button"
                  onClick={centerSelectedImageOrRow}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white text-sm disabled:opacity-40"
                  disabled={saving || !hasSelectedContentImage}
                  title="Center selected image or image row"
                >
                  Center
                </button>
                <button
                  type="button"
                  onClick={deleteSelectedContentImage}
                  className="px-3 py-1 bg-red-700 hover:bg-red-600 text-white text-sm disabled:opacity-40 disabled:hover:bg-red-700"
                  disabled={saving || !hasSelectedContentImage}
                  title="Delete selected image"
                >
                  Delete image
                </button>
                <button
                  type="button"
                  onMouseDown={saveEditorSelection}
                  onClick={() => contentImageInputRef.current?.click()}
                  className="px-3 py-1 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold"
                  disabled={saving}
                  title="Insert image"
                >
                  📷 Image
                </button>

                {hasSelectedContentImage && (
                  <input
                    type="text"
                    value={contentImageAlt}
                    onChange={(e) => updateSelectedContentImageAlt(e.target.value)}
                    className="min-w-56 flex-1 bg-gray-800 border border-gray-600 px-3 py-1 text-white text-sm focus:border-cyan-500 focus:outline-none"
                    placeholder="Describe the selected image"
                    aria-label="Selected image alt text"
                    title="Alt text for accessibility and image search"
                  />
                )}

                <div className="w-px bg-gray-500"></div>

                <button
                  type="button"
                  onClick={cleanCurrentContent}
                  className="px-3 py-1 bg-yellow-600 hover:bg-yellow-500 text-white text-sm"
                  disabled={saving}
                  title="Clean Word formatting"
                >
                  🧹 Clean
                </button>
              </div>

              <input
                ref={contentImageInputRef}
                id="contentImageInput"
                type="file"
                accept="image/*"
                onChange={insertImageIntoContent}
                className="hidden"
              />

              <div
                ref={contentRef}
                contentEditable
                className="blog-editor-surface w-full bg-gray-700 border border-gray-600 border-t-0 px-5 py-5 text-white focus:border-cyan-500 focus:outline-none min-h-96"
                suppressContentEditableWarning={true}
                onFocus={setDefaultParagraphs}
                onInput={syncEditorContent}
                onClick={handleEditorClick}
                onKeyDown={handleEditorKeyDown}
                onMouseUp={saveEditorSelection}
                onKeyUp={saveEditorSelection}
                onPaste={handlePaste}
              />

              <p className="text-xs text-gray-400 mt-2">
                Press Enter once for a new paragraph. Use the image placement
                menu before uploading an in-body image to make text wrap around it.
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={saveArticle}
                className="bg-green-600 text-white px-6 py-2 font-semibold hover:bg-green-500 transition-colors disabled:opacity-50"
                disabled={saving}
              >
                {saving
                  ? "SAVING..."
                  : editingId
                  ? "UPDATE ARTICLE"
                  : "SAVE ARTICLE"}
              </button>
              <button
                onClick={cancelEdit}
                className="bg-gray-600 text-white px-6 py-2 font-semibold hover:bg-gray-500 transition-colors"
                disabled={saving}
              >
                CANCEL
              </button>
            </div>
          </div>
        )}

        <div className="bg-gray-800 border border-gray-600 p-6">
          <h2 className="text-2xl font-bold text-cyan-400 mb-6">
            ARTICLES ({articles.filter((article) => isArticlePublished(article)).length} published, {articles.filter((article) => !isArticlePublished(article)).length} scheduled)
          </h2>

          {articles.length === 0 ? (
            <p className="text-gray-400">No articles published yet.</p>
          ) : (
            <div className="space-y-4">
              {articles.map((article) => (
                <div
                  key={article.id}
                  className="border border-gray-600 p-4 bg-gray-700"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {article.title}
                        {article.featured && (
                          <span className="ml-2 bg-yellow-500 text-gray-900 px-2 py-1 text-xs font-bold rounded">
                            FEATURED
                          </span>
                        )}
                        {!isArticlePublished(article) && (
                          <span className="ml-2 bg-blue-500 text-white px-2 py-1 text-xs font-bold rounded">
                            SCHEDULED
                          </span>
                        )}
                        {article.opinionArticle && (
                          <span className="ml-2 bg-orange-500 text-gray-900 px-2 py-1 text-xs font-bold rounded">
                            OPINION
                          </span>
                        )}
                      </h3>
                      <p className="text-gray-300 text-sm mb-2">
                        {article.excerpt}
                      </p>
                      <div className="text-xs text-gray-400">
                        By {article.author} | {article.date} |{" "}
                        {article.readTime}
                        {article.imageUrl && (
                          <span className="ml-2 text-green-400">
                            📷 Has Image
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => editArticle(article)}
                        className="bg-blue-600 text-white px-3 py-1 text-sm hover:bg-blue-500 transition-colors"
                        disabled={saving}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteArticle(article.id)}
                        className="bg-red-600 text-white px-3 py-1 text-sm hover:bg-red-500 transition-colors"
                        disabled={saving}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
